// Grid and axes helpers

import * as THREE from 'three';
import { mapPhysicalToThreeWithRotation } from '@adapters/coordinates';
import { GRID_LABEL_DEFAULTS } from '../config/defaults';
import type { ViewerContext } from '../app/context';
import type { ParamsState } from '../store/schema';
import type { ThemeDefaults } from '../config/defaults';

interface CreateAxisGridCtx {
    THREE: typeof import('three');
    scene: THREE.Scene;
    THEME: ThemeDefaults | null;
    AXIS_EXTENTS: { x: { min: number; max: number }; y: { min: number; max: number }; z: { min: number; max: number } };
    SHOW_GRID_XY: boolean;
    SHOW_GRID_XZ: boolean;
    SHOW_GRID_YZ: boolean;
    AXIS_GRID_MINOR_SPACING: number | null;
    AXIS_GRID_MAJOR_SPACING: number | null;
    SCENE_AXIS_GRID_MINOR_FOOT: number;
    SCENE_AXIS_GRID_MAJOR_FOOT: number;
    SCENE_AXIS_LABEL_EXTRA_PADDING: number;
    mapPhysicalToThreeWithRotation: (p: THREE.Vector3) => THREE.Vector3;
    axisGridXY: THREE.Object3D | null;
    axisGridXZ: THREE.Object3D | null;
    axisGridYZ: THREE.Object3D | null;
    gridLabels: THREE.Object3D[];
}

interface AxisGridResult {
    axisGridXY: THREE.Group;
    axisGridXZ: THREE.Group;
    axisGridYZ: THREE.Group;
    gridLabels: THREE.Mesh[];
}

/**
 * Rebuild the three coordinate-plane grids from current viewer + params.
 * Stores the new groups + labels back onto viewer.objects.axes.
 */
export function applyAxisGrid(viewer: ViewerContext, params: ParamsState): void {
    if (!viewer.three.scene || !viewer.grid.axisExtents) return;
    const out = createAxisGrid({
        THREE,
        scene: viewer.three.scene,
        THEME: viewer.theme.current,
        AXIS_EXTENTS: viewer.grid.axisExtents,
        SHOW_GRID_XY: params.showGridXY,
        SHOW_GRID_XZ: params.showGridXZ,
        SHOW_GRID_YZ: params.showGridYZ,
        AXIS_GRID_MINOR_SPACING: viewer.grid.minorSpacing,
        AXIS_GRID_MAJOR_SPACING: viewer.grid.majorSpacing,
        SCENE_AXIS_GRID_MINOR_FOOT: viewer.sceneMeta.axis.gridMinorFoot,
        SCENE_AXIS_GRID_MAJOR_FOOT: viewer.sceneMeta.axis.gridMajorFoot,
        SCENE_AXIS_LABEL_EXTRA_PADDING: viewer.sceneMeta.axis.labelExtraPadding,
        mapPhysicalToThreeWithRotation,
        axisGridXY: viewer.objects.axes.gridXY,
        axisGridXZ: viewer.objects.axes.gridXZ,
        axisGridYZ: viewer.objects.axes.gridYZ,
        gridLabels: viewer.objects.axes.labels,
    });
    viewer.objects.axes.gridXY = out.axisGridXY;
    viewer.objects.axes.gridXZ = out.axisGridXZ;
    viewer.objects.axes.gridYZ = out.axisGridYZ;
    viewer.objects.axes.labels.length = 0;
    viewer.objects.axes.labels.push(...out.gridLabels);
}

export function createAxisGrid(ctx: CreateAxisGridCtx): AxisGridResult {
    const { THREE, scene, THEME, AXIS_EXTENTS, SHOW_GRID_XY, SHOW_GRID_XZ, SHOW_GRID_YZ,
            AXIS_GRID_MINOR_SPACING, AXIS_GRID_MAJOR_SPACING,
            SCENE_AXIS_GRID_MINOR_FOOT, SCENE_AXIS_GRID_MAJOR_FOOT,
            SCENE_AXIS_LABEL_EXTRA_PADDING,
            mapPhysicalToThreeWithRotation } = ctx;

    let axisGridXY: THREE.Object3D | null = ctx.axisGridXY;
    let axisGridXZ: THREE.Object3D | null = ctx.axisGridXZ;
    let axisGridYZ: THREE.Object3D | null = ctx.axisGridYZ;
    let gridLabels: THREE.Object3D[] = ctx.gridLabels;
    if (axisGridXY && axisGridXY.parent) scene.remove(axisGridXY);
    if (axisGridXZ && axisGridXZ.parent) scene.remove(axisGridXZ);
    if (axisGridYZ && axisGridYZ.parent) scene.remove(axisGridYZ);
    if (Array.isArray(gridLabels) && gridLabels.length > 0) {
        gridLabels.forEach((lbl) => {
            if (!lbl) return;
            if (lbl.parent) lbl.parent.remove(lbl);
            const mesh = lbl as THREE.Mesh;
            if (mesh.geometry && typeof mesh.geometry.dispose === 'function') mesh.geometry.dispose();
            const mat = mesh.material;
            if (mat) {
                if (Array.isArray(mat)) {
                    mat.forEach((m) => {
                        const mm = m as THREE.MeshBasicMaterial;
                        if (mm.map) mm.map.dispose();
                        if (typeof mm.dispose === 'function') mm.dispose();
                    });
                } else {
                    const mm = mat as THREE.MeshBasicMaterial;
                    if (mm.map && typeof mm.map.dispose === 'function') mm.map.dispose();
                    if (typeof mm.dispose === 'function') mm.dispose();
                }
            }
        });
    }
    const newXY = new THREE.Group();
    const newXZ = new THREE.Group();
    const newYZ = new THREE.Group();
    const newLabels: THREE.Mesh[] = [];
    axisGridXY = newXY;
    axisGridXZ = newXZ;
    axisGridYZ = newYZ;
    gridLabels = newLabels;

    const gridColor = (THEME && THEME.scene && THEME.scene.grid && THEME.scene.grid.color) ? new THREE.Color(THEME.scene.grid.color) : new THREE.Color(0xffffff);
    const minorOpacity = (THEME && THEME.scene && THEME.scene.grid && typeof THEME.scene.grid.minorOpacity === 'number') ? THEME.scene.grid.minorOpacity : 0.15;
    const majorOpacity = (THEME && THEME.scene && THEME.scene.grid && typeof THEME.scene.grid.majorOpacity === 'number') ? THEME.scene.grid.majorOpacity : 0.35;
    const minorMaterial = new THREE.LineBasicMaterial({ color: gridColor.getHex(), transparent: true, opacity: minorOpacity });
    const majorMaterial = new THREE.LineBasicMaterial({ color: gridColor.getHex(), transparent: true, opacity: majorOpacity });

    const xMin = AXIS_EXTENTS.x.min, xMax = AXIS_EXTENTS.x.max;
    const yMin = AXIS_EXTENTS.y.min, yMax = AXIS_EXTENTS.y.max;
    const zMin = AXIS_EXTENTS.z.min, zMax = AXIS_EXTENTS.z.max;
    const minor = AXIS_GRID_MINOR_SPACING || 1;
    const major = AXIS_GRID_MAJOR_SPACING || (minor * 5);
    const labelOffset = 0.3 + (typeof SCENE_AXIS_LABEL_EXTRA_PADDING === 'number' ? SCENE_AXIS_LABEL_EXTRA_PADDING : 0);

    function isMajorGrid(value: number, step: number): boolean { const scaled = value / step; return Math.abs(scaled - Math.round(scaled)) < 1e-6; }
    function firstGrid(minVal: number, step: number): number { return Math.ceil(minVal / step) * step; }
    function createGridLabel(text: string, position: THREE.Vector3): THREE.Mesh {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = 256; canvas.height = 128;
        if (context) {
            context.clearRect(0, 0, 256, 128);
            context.fillStyle = (THEME && THEME.scene && THEME.scene.gridLabelColor) ? THEME.scene.gridLabelColor : '#808080';
            const baseWorldWidth = GRID_LABEL_DEFAULTS.widthWorld ?? 1.2;
            const worldWidth = baseWorldWidth;
            const fontPx = Math.max(28, Math.round(70 * (worldWidth / baseWorldWidth)));
            context.font = `${GRID_LABEL_DEFAULTS.fontWeight} ${fontPx}px ${GRID_LABEL_DEFAULTS.fontFamily}`;
            context.textAlign = 'center'; context.textBaseline = 'middle';
            context.fillText(text, 128, 64);
        }
        const baseWorldWidth = GRID_LABEL_DEFAULTS.widthWorld ?? 1.2;
        const worldWidth = baseWorldWidth;
        const texture = new THREE.CanvasTexture(canvas); texture.premultiplyAlpha = true; texture.generateMipmaps = false; texture.minFilter = THREE.LinearFilter; texture.magFilter = THREE.LinearFilter;
        const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true, opacity: GRID_LABEL_DEFAULTS.opacity, depthTest: GRID_LABEL_DEFAULTS.depthTest, depthWrite: GRID_LABEL_DEFAULTS.depthWrite, polygonOffset: GRID_LABEL_DEFAULTS.polygonOffset, polygonOffsetFactor: GRID_LABEL_DEFAULTS.polygonOffsetFactor, alphaTest: GRID_LABEL_DEFAULTS.alphaTest });
        const geometry = new THREE.PlaneGeometry(worldWidth, Math.max(0.5, (GRID_LABEL_DEFAULTS.heightScale ?? 0.6) * (worldWidth / baseWorldWidth)));
        const label = new THREE.Mesh(geometry, material);
        label.position.copy(position);
        return label;
    }
    function createGridLabelPhysical(text: string, physX: number, physY: number, physZ: number): void {
        const posThree = mapPhysicalToThreeWithRotation(new THREE.Vector3(physX, physY, physZ));
        const label = createGridLabel(text, posThree);
        newLabels.push(label);
        scene.add(label);
    }
    function createLineFromPhysical(startPhys: THREE.Vector3, endPhys: THREE.Vector3, material: THREE.LineBasicMaterial): THREE.Line {
        const startThree = mapPhysicalToThreeWithRotation(startPhys);
        const endThree = mapPhysicalToThreeWithRotation(endPhys);
        const geom = new THREE.BufferGeometry().setFromPoints([startThree, endThree]);
        return new THREE.Line(geom, material);
    }

    if (SHOW_GRID_XY) {
        for (let xVal = firstGrid(xMin, minor); xVal <= xMax + 1e-9; xVal += minor) {
            const isMaj = isMajorGrid(xVal, major);
            const line = createLineFromPhysical(new THREE.Vector3(xVal, yMin, 0), new THREE.Vector3(xVal, yMax + (isMaj ? SCENE_AXIS_GRID_MAJOR_FOOT : SCENE_AXIS_GRID_MINOR_FOOT), 0), isMaj ? majorMaterial : minorMaterial);
            newXY.add(line); if (isMaj) createGridLabelPhysical(`${xVal}`, xVal, yMax + labelOffset, 0);
        }
        for (let yVal = firstGrid(yMin, minor); yVal <= yMax + 1e-9; yVal += minor) {
            const isMaj = isMajorGrid(yVal, major);
            const line = createLineFromPhysical(new THREE.Vector3(xMin, yVal, 0), new THREE.Vector3(xMax + (isMaj ? SCENE_AXIS_GRID_MAJOR_FOOT : SCENE_AXIS_GRID_MINOR_FOOT), yVal, 0), isMaj ? majorMaterial : minorMaterial);
            newXY.add(line); if (isMaj) createGridLabelPhysical(`${yVal}`, xMax + labelOffset, yVal, 0);
        }
        scene.add(newXY);
    }
    if (SHOW_GRID_XZ) {
        for (let xVal = firstGrid(xMin, minor); xVal <= xMax + 1e-9; xVal += minor) {
            const isMaj = isMajorGrid(xVal, major);
            const line = createLineFromPhysical(new THREE.Vector3(xVal, 0, zMin), new THREE.Vector3(xVal, 0, zMax + (isMaj ? SCENE_AXIS_GRID_MAJOR_FOOT : SCENE_AXIS_GRID_MINOR_FOOT)), isMaj ? majorMaterial : minorMaterial);
            newXZ.add(line); if (isMaj) createGridLabelPhysical(`${xVal}`, xVal, 0, zMax + labelOffset);
        }
        for (let zVal = firstGrid(zMin, minor); zVal <= zMax + 1e-9; zVal += minor) {
            const isMaj = isMajorGrid(zVal, major);
            const line = createLineFromPhysical(new THREE.Vector3(xMin, 0, zVal), new THREE.Vector3(xMax + (isMaj ? SCENE_AXIS_GRID_MAJOR_FOOT : SCENE_AXIS_GRID_MINOR_FOOT), 0, zVal), isMaj ? majorMaterial : minorMaterial);
            newXZ.add(line); if (isMaj) createGridLabelPhysical(`${zVal}`, xMax + labelOffset, 0, zVal);
        }
        scene.add(newXZ);
    }
    if (SHOW_GRID_YZ) {
        for (let yVal = firstGrid(yMin, minor); yVal <= yMax + 1e-9; yVal += minor) {
            const isMaj = isMajorGrid(yVal, major);
            const line = createLineFromPhysical(new THREE.Vector3(0, yVal, zMin), new THREE.Vector3(0, yVal, zMax + (isMaj ? SCENE_AXIS_GRID_MAJOR_FOOT : SCENE_AXIS_GRID_MINOR_FOOT)), isMaj ? majorMaterial : minorMaterial);
            newYZ.add(line); if (isMaj) createGridLabelPhysical(`${yVal}`, 0, yVal, zMax + labelOffset);
        }
        for (let zVal = firstGrid(zMin, minor); zVal <= zMax + 1e-9; zVal += minor) {
            const isMaj = isMajorGrid(zVal, major);
            const line = createLineFromPhysical(new THREE.Vector3(0, yMin, zVal), new THREE.Vector3(0, yMax + (isMaj ? SCENE_AXIS_GRID_MAJOR_FOOT : SCENE_AXIS_GRID_MINOR_FOOT), zVal), isMaj ? majorMaterial : minorMaterial);
            newYZ.add(line); if (isMaj) createGridLabelPhysical(`${zVal}`, 0, yMax + labelOffset, zVal);
        }
        scene.add(newYZ);
    }

    return { axisGridXY: newXY, axisGridXZ: newXZ, axisGridYZ: newYZ, gridLabels: newLabels };
}


